import { FilterSelectOption } from '../../../atomic/molecules/FlairDropdown/types';

type NamedEntity = {
  Id: string;
  Name: string;
  // Admins retire a location/department by unchecking Active. Retired ones stay
  // on the employees that reference them but must not be offered as filters.
  flair__Active__c?: boolean | null;
};

type EmployeeWithLocation = {
  location?: NamedEntity | null;
};

type EmployeeWithDepartment = {
  department?: NamedEntity | null;
};

type EmployeeWithTeams = {
  teams?: readonly NamedEntity[] | null;
};

type EmployeeForFilters = EmployeeWithLocation &
  EmployeeWithDepartment &
  EmployeeWithTeams;

const createFilterOption = (entity: NamedEntity): FilterSelectOption => ({
  label: entity.Name,
  value: entity.Id,
});

const extractUniqueOptions = (
  entities: (NamedEntity | null | undefined)[],
): FilterSelectOption[] => {
  const map = new Map<string, FilterSelectOption>();
  entities.forEach((entity) => {
    if (entity?.Id && entity?.Name && entity.flair__Active__c !== false) {
      map.set(entity.Id, createFilterOption(entity));
    }
  });
  return Array.from(map.values());
};

export const extractLocationOptions = (
  employees: readonly EmployeeWithLocation[],
): FilterSelectOption[] => {
  return extractUniqueOptions(employees.map((employee) => employee.location));
};

export const extractDepartmentOptions = (
  employees: readonly EmployeeWithDepartment[],
): FilterSelectOption[] => {
  return extractUniqueOptions(employees.map((employee) => employee.department));
};

export const extractTeamOptions = (
  employees: readonly EmployeeWithTeams[],
): FilterSelectOption[] => {
  const allTeams: (NamedEntity | null | undefined)[] = [];
  employees.forEach((employee) => {
    if (employee.teams) {
      allTeams.push(...employee.teams);
    }
  });
  return extractUniqueOptions(allTeams);
};

export const extractFilterOptions = (
  employees: readonly EmployeeForFilters[],
): {
  locations: FilterSelectOption[];
  departments: FilterSelectOption[];
  teams: FilterSelectOption[];
} => {
  return {
    locations: extractLocationOptions(employees),
    departments: extractDepartmentOptions(employees),
    teams: extractTeamOptions(employees),
  };
};
